import { LotStatus } from '../../constants/index.js';

/**
 * Group lots into one row per item (projectDocId + pPartDocId) for the
 * By-item overview: per status, how many bags and how many parts remain
 * in them, plus a usable-parts total across the row — quarantined and
 * awaiting-inspection bags are excluded from that total, matching the
 * statuses every consumption path rejects at scan time.
 * Negative remaining quantities (over-consumption — counts are
 * advisory) clamp to zero so a drained bag can't subtract. Rows and
 * their lots keep the input order (callers pass query-ordered lots,
 * newest first), so the overview can expand a row into its bags
 * without another fetch; labeling is the caller's job.
 *
 * @param {Array<object>} lots - Lot models.
 * @returns {Array<{
 *   key: string,
 *   projectDocId: string,
 *   pPartDocId: string,
 *   byStatus: Object<string, {bags: number, pcs: number}>,
 *   usableTotal: number,
 *   lots: Array<object>,
 * }>}
 */
export default function groupLotsByItem(lots) {
  const rowsByKey = new Map();
  (lots || []).forEach((lot) => {
    const key = `${lot.projectDocId}/${lot.pPartDocId}`;
    if (!rowsByKey.has(key)) {
      rowsByKey.set(key, {
        key,
        projectDocId: lot.projectDocId,
        pPartDocId: lot.pPartDocId,
        byStatus: {},
        usableTotal: 0,
        lots: [],
      });
    }
    const row = rowsByKey.get(key);
    const bucket = row.byStatus[lot.status] || { bags: 0, pcs: 0 };
    const remaining = Math.max(lot.remainingQty ?? 0, 0);
    bucket.bags += 1;
    bucket.pcs += remaining;
    row.byStatus[lot.status] = bucket;
    if (![LotStatus.QUARANTINED, LotStatus.AWAITING_INSPECTION].includes(lot.status)) {
      row.usableTotal += remaining;
    }
    row.lots.push(lot);
  });
  return [...rowsByKey.values()];
}
